import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from "@angular/core";
import { ofType, Actions } from "@ngrx/effects";
import { map, take, takeUntil } from "rxjs/operators";
import {
  InvoiceReportsSearchParams,
  Receipt,
} from "src/app/shared/models/invoices.model";
import { ServiceMetaData } from "src/app/shared/models/service-meta.model";
import { Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { SharedService } from "src/app/shared/services/shared.service";
import { TranslateService } from "@ngx-translate/core";
import { ReportsService } from "src/app/shared/services/reports.service";
import { PaginationParams } from "src/app/shared/models/common";
import { AuthService } from "src/app/shared/services/auth.service";
import { RUN_REPORTS_SEARCH } from "src/app/shared/ngrx/ngrx-actions";
import {
  curReportReceipts,
  receiptsSearchMetadata,
  reportsSearchParams,
} from "src/app/shared/ngrx/ngrx-selectors";
import { CommonModule } from "@angular/common";
import { LoaderComponent } from "src/app/shared/components/ui/loader/loader.component";
import { SpinnerComponent } from "src/app/shared/components/ui/spinner/spinner.component";
import { TableComponent } from "src/app/shared/components/ui/table/table.component";

@Component({
  selector: "receipts",
  templateUrl: "./receipts.component.html",
  styleUrls: ["./receipts.component.sass"],
  standalone: true,
  imports: [CommonModule, LoaderComponent, TableComponent, SpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReceiptsComponent implements OnInit {
  destroy$ = new Subject();
  searchParams: InvoiceReportsSearchParams;
  numberOfRows: number = 10;
  loading: boolean;
  clinicUserSw: boolean;

  @Output() onDocPdfOpen = new EventEmitter<any>();

  get reportsSearchParams(): InvoiceReportsSearchParams {
    return this.store.selectSignal(reportsSearchParams)();
  }

  get searchMetaData(): ServiceMetaData {
    return this.store.selectSignal(receiptsSearchMetadata)() || {};
  }

  get data(): Receipt[] {
    return this.store.selectSignal(curReportReceipts)();
  }

  constructor(
    private readonly store: Store,
    private reportsService: ReportsService,
    private authService: AuthService,
    private actions$: Actions,
    private cdr: ChangeDetectorRef
  ) {
    this.clinicUserSw = this.authService.currentUser.org == "CLINIC";

    this.actions$
      .pipe(
        ofType(RUN_REPORTS_SEARCH),
        takeUntil(this.destroy$),
        map(({}) => {
          this.runSearch(true);
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    if (this.store.selectSignal(reportsSearchParams)()) {
      this.runSearch(true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(1);
  }

  runSearch(newSearch: boolean) {
    if (newSearch) {
      this.searchParams = {
        rangeTo: this.numberOfRows,
        rangeFrom: 1,
        searchId: null,
      };
    }

    this.searchParams.clinic = this.reportsSearchParams.clinic;
    this.searchParams.documentId = this.reportsSearchParams.documentId;
    this.searchParams.fromDate = this.reportsSearchParams.fromDate;
    this.searchParams.toDate = this.reportsSearchParams.toDate;

    this.loading = true;
    this.cdr.markForCheck();
    this.reportsService.getClientReceipts(this.searchParams).subscribe(
      () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
      () => {
        this.loading = false;
        this.cdr.markForCheck();
      }
    );
  }

  loadDataOnPagination(event: PaginationParams) {
    if (
      !this.data ||
      (event.rangeFrom == this.searchParams.rangeFrom &&
        event.rangeTo == this.searchParams.rangeTo)
    )
      return;

    this.searchParams.rangeFrom = event.rangeFrom;
    this.searchParams.rangeTo = event.rangeTo;
    this.searchParams.searchId = this.searchMetaData.searchId;

    this.runSearch(false);
  }

  onRowSelect(receipt: Receipt) {
    receipt.docLoading = true;
    this.cdr.markForCheck();
    this.reportsService.getReceiptPdfUrl(receipt.rcptId).subscribe(
      (url) => {
        receipt.docLoading = false;
        this.cdr.markForCheck();
        if (url) {
          this.onDocPdfOpen.emit({ docNum: receipt.rcptId, url });
        }
      },
      () => {
        receipt.docLoading = false;
        this.cdr.markForCheck();
      }
    );
  }
}
